import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { CartProvider } from "@/contexts/CartContext";
import { AuthRedirectHandler } from "@/components/AuthRedirectHandler";
import CookieConsent from "@/components/CookieConsent";
import ErrorBoundary from "@/components/ErrorBoundary";
// Import Index directly (not lazy) to ensure it loads in production
import Index from "./pages/Index";
const Auth = lazy(() => import("./pages/Auth"));
const OAuthDebug = lazy(() => import("./pages/OAuthDebug"));
const HealthCheck = lazy(() => import("./pages/HealthCheck"));
const CartSync = lazy(() => import("./pages/CartSync"));
const Solutions = lazy(() => import("./pages/Solutions"));
const CompanyRegistration = lazy(() => import("./pages/CompanyRegistration"));
const OrderSummary = lazy(() => import("./pages/OrderSummary"));
const StackSelection = lazy(() => import("./pages/StackSelection"));
const StackSelectionDetail = lazy(() => import("./pages/StackSelectionDetail"));
const Calculator = lazy(() => import("./pages/Calculator"));
const ShareEstimate = lazy(() => import("./pages/ShareEstimate"));
const ShareEstimatePublic = lazy(() => import("./pages/ShareEstimatePublic"));
const PositionDetail = lazy(() => import("./pages/PositionDetail"));
const Briefing = lazy(() => import("./pages/Briefing"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const MySquads = lazy(() => import("./pages/MySquads"));
const SquadDetails = lazy(() => import("./pages/SquadDetails"));
const NotFound = lazy(() => import("./pages/NotFound"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const HybridDelivery = lazy(() => import("./pages/HowItWorks/HybridDelivery"));
const AISourcing = lazy(() => import("./pages/HowItWorks/AISourcing"));
const PoweredByLuby = lazy(() => import("./pages/Why/PoweredByLuby"));
const BankingQuality = lazy(() => import("./pages/Why/BankingQuality"));
const TrackRecord = lazy(() => import("./pages/Why/TrackRecord"));
const ClientSuccess = lazy(() => import("./pages/Insights/ClientSuccess"));
const Articles = lazy(() => import("./pages/Insights/Articles"));
const Insights = lazy(() => import("./pages/Insights/Insights"));
const About = lazy(() => import("./pages/About"));
const Story = lazy(() => import("./pages/About/Story"));
const Leadership = lazy(() => import("./pages/About/Leadership"));
const Careers = lazy(() => import("./pages/About/Careers"));
const BookCall = lazy(() => import("./pages/Contact/BookCall"));
const Contact = lazy(() => import("./pages/Contact"));
const SecurityCompliance = lazy(() => import("./pages/SecurityCompliance"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Infrastructure = lazy(() => import("./pages/Infrastructure"));
const Development = lazy(() => import("./pages/Development"));
const QA = lazy(() => import("./pages/QA"));
const CyberSecurity = lazy(() => import("./pages/CyberSecurity"));
const ArtificialIntelligence = lazy(() => import("./pages/ArtificialIntelligence"));
const ProductManagement = lazy(() => import("./pages/ProductManagement"));
const UIUX = lazy(() => import("./pages/UIUX"));
const ProjectManagement = lazy(() => import("./pages/ProjectManagement"));
const Data = lazy(() => import("./pages/Data"));
const Fintech = lazy(() => import("./pages/Fintech"));
const Healthcare = lazy(() => import("./pages/Healthcare"));
const Ecommerce = lazy(() => import("./pages/Ecommerce"));
const Education = lazy(() => import("./pages/Education"));
const Manufacturing = lazy(() => import("./pages/Manufacturing"));
const RealEstate = lazy(() => import("./pages/RealEstate"));
const Logistics = lazy(() => import("./pages/Logistics"));
const Media = lazy(() => import("./pages/Media"));
const Government = lazy(() => import("./pages/Government"));
const Startups = lazy(() => import("./pages/Startups"));
const StyleGuide = lazy(() => import("./pages/StyleGuide"));
const Industries = lazy(() => import("./pages/Industries"));

// Optimize QueryClient to reduce main thread blocking and script evaluation time
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Reduce refetch on window focus to minimize main thread work
      refetchOnWindowFocus: false,
      // Stale time to reduce unnecessary re-fetches
      staleTime: 5 * 60 * 1000, // 5 minutes
      // Retry less aggressively to reduce network overhead
      retry: 1,
      // Disable refetch on reconnect to reduce main thread work
      refetchOnReconnect: false,
      // Reduce refetch interval overhead
      refetchInterval: false,
    },
    mutations: {
      // Reduce retry attempts for mutations
      retry: 1,
    },
  },
});

const PageLoader = () => (
  <div className="flex min-h-screen items-center justify-center bg-[#0F2341] text-white">
    <p className="text-lg font-semibold">Loading experience...</p>
  </div>
);

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <CartProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <AuthRedirectHandler />
              <CookieConsent />
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/auth" element={<Auth />} />
          <Route path="/oauth-debug" element={<OAuthDebug />} />
          <Route path="/healthcheck" element={<HealthCheck />} />
          <Route path="/cart-sync" element={<CartSync />} />
          <Route path="/solutions/:type" element={<Solutions />} />
          <Route path="/company-registration" element={<CompanyRegistration />} />
          <Route path="/summary" element={<OrderSummary />} />
          <Route path="/stack-selection" element={<StackSelection />} />
          <Route path="/stack-selection/:slug" element={<StackSelectionDetail />} />
          <Route path="/calculator" element={<Calculator />} />
          <Route path="/share-estimate" element={<ShareEstimate />} />
          <Route path="/share/:slug" element={<ShareEstimatePublic />} />
          <Route path="/position/:slug" element={<PositionDetail />} />
          <Route path="/briefing" element={<Briefing />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/my-squads" element={<MySquads />} />
          <Route path="/squad-details" element={<SquadDetails />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/how-it-works/hybrid-delivery" element={<HybridDelivery />} />
          <Route path="/how-it-works/ai-sourcing" element={<AISourcing />} />
          <Route path="/why/powered-by-luby" element={<PoweredByLuby />} />
          <Route path="/why/banking-quality" element={<BankingQuality />} />
          <Route path="/why/track-record" element={<TrackRecord />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/insights/client-success" element={<ClientSuccess />} />
          <Route path="/insights/articles" element={<Articles />} />
          <Route path="/about" element={<About />} />
          <Route path="/about/story" element={<Story />} />
          <Route path="/about/leadership" element={<Leadership />} />
          <Route path="/about/careers" element={<Careers />} />
          <Route path="/contact/book-call" element={<BookCall />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/security-compliance" element={<SecurityCompliance />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/style-guide" element={<StyleGuide />} />
          {/* Expertises Routes */}
          <Route path="/stack-selection/infrastructure" element={<Infrastructure />} />
          <Route path="/stack-selection/development" element={<Development />} />
          <Route path="/stack-selection/qa" element={<QA />} />
          <Route path="/stack-selection/cybersecurity" element={<CyberSecurity />} />
          <Route path="/stack-selection/artificial-intelligence" element={<ArtificialIntelligence />} />
          <Route path="/stack-selection/product-management" element={<ProductManagement />} />
          <Route path="/stack-selection/ui-ux" element={<UIUX />} />
          <Route path="/stack-selection/project-management" element={<ProjectManagement />} />
          <Route path="/stack-selection/data" element={<Data />} />
          {/* Industries Routes */}
          <Route path="/industries" element={<Industries />} />
          <Route path="/fintech" element={<Fintech />} />
          <Route path="/solutions/fintech" element={<Fintech />} />
          <Route path="/healthcare" element={<Healthcare />} />
          <Route path="/solutions/healthcare" element={<Healthcare />} />
          <Route path="/ecommerce" element={<Ecommerce />} />
          <Route path="/solutions/ecommerce" element={<Ecommerce />} />
          <Route path="/education" element={<Education />} />
          <Route path="/solutions/education" element={<Education />} />
          <Route path="/manufacturing" element={<Manufacturing />} />
          <Route path="/solutions/manufacturing" element={<Manufacturing />} />
          <Route path="/real-estate" element={<RealEstate />} />
          <Route path="/solutions/real-estate" element={<RealEstate />} />
          <Route path="/logistics" element={<Logistics />} />
          <Route path="/solutions/logistics" element={<Logistics />} />
          <Route path="/media" element={<Media />} />
          <Route path="/solutions/media" element={<Media />} />
          <Route path="/government" element={<Government />} />
          <Route path="/solutions/government" element={<Government />} />
          <Route path="/startups" element={<Startups />} />
          <Route path="/solutions/startups" element={<Startups />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </CartProvider>
      </HelmetProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
